// Client-side sorting of the loaded rows.
//
// Two rules the rest of the page depends on. A null is its own group and always sorts last, in both
// directions: a missing score is the signal this endpoint exists to show, and letting it fall to the
// bottom as if it were the smallest number would read as "worst" rather than "absent". And the sort
// is stable, so rows that tie keep the server's order rather than shuffling between renders.
//
// Sorting the whole feed is a different problem: only the rows the request asked for are here, so
// "the top 50 companies in the corpus" needs server-side ordering. This sorts what's on screen.

export const SORT_DIRECTIONS = { ASC: 'asc', DESC: 'desc' };

const compareText = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });

const compareNumbers = (a, b) => a - b;

const timeValue = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
};

// The company cell reads "SYM — Name", so it sorts by what a reader sees first — deliberately the
// visible label and not company_id, even though the symbol is a point-in-time value: a column
// ordered by an id nobody can see reads as unordered. The id only breaks ties (see compareCompanyId).
const companyValue = (row) =>
  row.symbol ||
  row.companyName ||
  (row.companyId !== null && row.companyId !== undefined ? String(row.companyId) : null);

// Index membership sorts by how many indices a row belongs to, not by the joined label: the cell
// reads "Dow 30, S&P 500" or "None", and alphabetising that would bury "None" among the index names
// instead of parking it at one end. A row from a feed that carries no flags at all has no count.
const membershipValue = (row) => (row.hasMemberships ? (row.memberships || []).length : null);

export const SORT_FIELDS = {
  company: { value: companyValue, compare: compareText },
  score: { value: (row) => row.score, compare: compareNumbers },
  // Both derived, and both inheriting the rules above rather than restating them. Two things follow
  // from that and are worth naming:
  //
  // A row with no earlier occasion and a row whose earlier occasion scored nothing sort into the SAME
  // absent group. A sort cannot express the difference — there is one slot per row — and the cell's
  // tooltip already does, which is where the distinction belongs.
  //
  // A change of ZERO is not absent. isEmpty tests for null/undefined/'', so 0 sorts as the number it
  // is, between the risers and the fallers. That is the whole point of keeping it as 0 rather than
  // collapsing an unmoved score into a blank.
  prevScore: { value: (row) => row.prevScore, compare: compareNumbers },
  scoreDelta: { value: (row) => row.scoreDelta, compare: compareNumbers },
  // The company's net change across the occasions in this read, attached by scoreSeries before the
  // sort runs. Unlike the pair above it is meaningful in BOTH grains — an aggregated row inherits its
  // company's net — so FeedRowsTable does not drop a Movement sort when the rows are averaged.
  //
  // Null for a company with fewer than two scored occasions, which isEmpty puts in the absent group.
  // A 0 would sort it among the companies that genuinely didn't move, which is a different claim.
  movement: { value: (row) => row.movementNet, compare: compareNumbers },
  knowableAt: { value: (row) => timeValue(row.knowableAt), compare: compareNumbers },
  sector: { value: (row) => row.sector, compare: compareText },
  marketCap: { value: (row) => row.marketCap, compare: compareNumbers },
  memberships: { value: membershipValue, compare: compareNumbers },
};

// Marker columns are per-feed, so their sort fields can't be a fixed list — they're named after the
// member whose values the column holds. A member that contributed no value to a row sorts as absent,
// the same as any other missing number.
export const INPUT_SORT_PREFIX = 'input:';

export const inputSortField = (inputKey) => `${INPUT_SORT_PREFIX}${inputKey}`;

const inputSortDefinition = (field) => {
  const inputKey = field.slice(INPUT_SORT_PREFIX.length);
  if (!inputKey) return null;
  return {
    compare: compareNumbers,
    value: (row) => (row.members || []).find((input) => input.key === inputKey)?.value ?? null,
  };
};

const sortDefinition = (field) => {
  if (typeof field !== 'string') return null;
  if (field.startsWith(INPUT_SORT_PREFIX)) return inputSortDefinition(field);
  return SORT_FIELDS[field] || null;
};

const isEmpty = (value) => value === null || value === undefined || value === '';

// company_id is the only stable identity a row carries: `ticker` is resolved as-of the row's
// knowable_at, so one company can appear in a single result under two symbols, and two different
// companies can share a name. Breaking ties on the id keeps one company's rows contiguous instead of
// letting a same-named neighbour interleave with them.
//
// Not multiplied by the sort direction on purpose — a tiebreak that flips with the arrow is not a
// stabiliser. An id-less row sorts after the identified ones rather than tying with them.
const compareCompanyId = (a, b) => {
  const aId = a?.companyId;
  const bId = b?.companyId;
  if (isEmpty(aId) && isEmpty(bId)) return 0;
  if (isEmpty(aId)) return 1;
  if (isEmpty(bId)) return -1;
  if (typeof aId === 'number' && typeof bId === 'number') return aId - bId;
  return compareText(aId, bId);
};

// The order two equal rows settle into: identity first, then the position the server sent them in.
const breakTie = (a, b) => {
  const byCompany = compareCompanyId(a.row, b.row);
  return byCompany !== 0 ? byCompany : a.index - b.index;
};

// A column sorts DESCENDING on the first press, then toggles. Scores, row counts and dates are all
// read biggest-or-newest-first, so descending is the answer to "sort this" often enough that making
// it the first press saves a second one nearly every time.
//
// There is deliberately no third state: once a column is sorted, pressing it again always flips the
// direction rather than dropping back to server order. Nor is server order reachable by re-running
// the read any more — the table opens on DEFAULT_SORT below. It survives only for the feeds that
// default declines to sort, and if it is ever wanted back as a state an analyst can ASK for, that is
// a third press or a control of its own rather than a return of this one.
export const nextSort = (current, field) => {
  if (!current || current.field !== field) return { field, direction: SORT_DIRECTIONS.DESC };
  return {
    field,
    direction: current.direction === SORT_DIRECTIONS.DESC ? SORT_DIRECTIONS.ASC : SORT_DIRECTIONS.DESC,
  };
};

export const applyRowSort = (rows, sort) => {
  const list = rows || [];
  const definition = sort && sortDefinition(sort.field);
  if (!definition) return list;

  const factor = sort.direction === SORT_DIRECTIONS.DESC ? -1 : 1;

  // Decorated with the original index so ties fall back to server order explicitly, rather than
  // relying on the engine's sort being stable. company_id is tried first — server order is stable
  // only within one response, and a re-read can hand back equal rows the other way round.
  //
  // The two rows-are-equal branches below take the SAME tiebreak. Two rows that both lack a sector
  // are as tied as two rows that share one, and on most feeds the absent group is the bigger of the
  // two: leaving it in server order alone would mean the half of the table an analyst is least able
  // to eyeball reorders itself between reads.
  return list
    .map((row, index) => ({ row, index, value: definition.value(row) }))
    .sort((a, b) => {
      const aEmpty = isEmpty(a.value);
      const bEmpty = isEmpty(b.value);
      if (aEmpty && bEmpty) return breakTie(a, b);
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      const compared = definition.compare(a.value, b.value);
      if (compared !== 0) return compared * factor;
      return breakTie(a, b);
    })
    .map((entry) => entry.row);
};

// A sort only survives a new result if the column is still there to sort by — a feed with no sector
// would otherwise keep an invisible sort applied.
export const isSortFieldPresent = (rows, field) => {
  const definition = sortDefinition(field);
  if (!definition) return false;
  return (rows || []).some((row) => !isEmpty(definition.value(row)));
};

// The order a table opens in.
//
// A row is one (company, knowable-at), so a feed is a stream of occasions, and the question an
// analyst brings to a fresh one is nearly always "what is newest". Server order answered that only
// by coincidence — it is whatever the engine emitted, which reads as unordered — so the first paint
// now answers it outright rather than waiting to be asked.
//
// Descending for the reason nextSort already gives: dates are read newest-first.
export const DEFAULT_SORT = { field: 'knowableAt', direction: SORT_DIRECTIONS.DESC };

// The sort a table falls back to whenever the one it had stops being real: on load, when a new
// result has no column the old sort named, and when aggregating empties one.
//
// It applies wherever there is a timestamp to apply it to, including a feed read at a single
// instant. Every row sharing one occasion means the comparison ties on every pair and applyRowSort
// falls through to breakTie, so such a table comes out ranked by company_id. That is a deliberate
// call: one order for every feed is worth more than avoiding the tiebreak on the feeds where the
// dates happen to be equal, and it is the same tiebreak any pressed sort already takes.
//
// The two things it still declines are the ones where there is nothing to apply. No row carries a
// timestamp, so there is no field to order by at all. Or the column is one the analyst has turned
// off: `knowableAt` is a CORE column, but canHideColumn lets it be hidden like any other, and a
// table silently reordering itself by a column it is not drawing has no header to announce the order
// and — nextSort having no third state — no press that could undo it.
export const fallbackSort = (rows, hiddenColumns = []) => {
  if ((hiddenColumns || []).includes(DEFAULT_SORT.field)) return null;
  return isSortFieldPresent(rows, DEFAULT_SORT.field) ? DEFAULT_SORT : null;
};

// What the header button announces, since the visual arrow can't be read aloud. Marker columns pass
// their own label — the member's display name, which the sort field only knows as a key.
export const sortStateLabel = (sort, field, labelOverride) => {
  // The caller supplies the label. This module used to keep a second table of them, which was dead
  // — every render site passes the column model's label, so the local copy could never be reached —
  // and it had already drifted from columns.js ('Knowable at' against 'Knowable at (UTC)'). One
  // vocabulary, owned by the column model; `field` is the last-resort name, as it always was for
  // the per-member sort fields, which never had an entry here.
  const label = labelOverride || field;
  if (!sort || sort.field !== field) return `Sort by ${label}`;
  return sort.direction === SORT_DIRECTIONS.ASC
    ? `${label}, sorted ascending. Sort descending`
    : `${label}, sorted descending. Sort ascending`;
};
